import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageProps } from "../types/auth";

import styles from './styles/OrganizationsPage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Organization {
  org_id: number;
  name: string;
  created_at: string;
}

const OrganizationsPage: React.FC<PageProps> = ({ user, token, selectedOrgID }) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/organizations`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch organizations.");
          return;
        }

        const data = await response.json();
        setOrganizations(Array.isArray(data) ? data : []);
      } catch (err) {
        setError("An unexpected error occurred.");
      }
    };

    fetchOrganizations();
  }, [token, selectedOrgID]);

  const handleDelete = async (orgID: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/organizations/${orgID}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
        },
      });

      if (!response.ok) {
        console.error("Failed to delete organization.");
        return;
      }

      // Update the organizations list after deletion
      setOrganizations(organizations.filter((org) => org.org_id !== orgID));
    } catch (err) {
      console.error("Error deleting organization:", err);
    }
  };

  const navigateToCreate = () => {
    navigate("/organizations/create");
  };

  if (error) {
    return <p className={styles.errorMessage}>{error}</p>;
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Organizations</h1>
      <button onClick={navigateToCreate} className={styles.createButton}>Create Organization</button>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.tableHeader}>Name</th>
            <th className={styles.tableHeader}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {organizations.length > 0 ? (
            organizations.map((org) => (
              <tr key={org.org_id} className={styles.tableRow}>
                <td className={styles.tableCell}>{org.name}</td>
                <td className={styles.tableCell}>
                  <button onClick={() => handleDelete(org.org_id)} className={styles.deleteButton}>Delete</button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={2} className={styles.tableCell}>No organizations found.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default OrganizationsPage; 
